import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Plus, Trash2, Power, PowerOff, Save, X, ExternalLink, Rss } from "lucide-react";
import AdminNewsCreate from "@/components/admin/AdminNewsCreate";

const CATEGORIES = [
  "World", "Politics", "Business", "Technology", "Science", "Health",
  "Environment", "Entertainment", "Sports", "Good News", "Odd News",
  "Weather", "War", "Disasters", "News You Can Use",
];

const EMPTY = { name: "", url: "", category: "World", country: "Global", weight: 3, enabled: true };

export default function AdminNewsSources() {
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [srcs, r] = await Promise.all([
        http.get("/admin/news/sources"),
        http.get("/admin/news/runs", { params: { limit: 5 } }),
      ]);
      setSources(srcs.data.items || []);
      setRuns(r.data.items || []);
    } catch {
      toast.error("Failed to load news sources");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.url) return toast.error("Name and URL required");
    try {
      await http.post("/admin/news/sources", form);
      toast.success("Source added");
      setForm(EMPTY); setAdding(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const saveEdit = async () => {
    try {
      await http.patch(`/admin/news/sources/${editingId}`, editForm);
      toast.success("Saved");
      setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Existing articles from this source remain.`)) return;
    try { await http.delete(`/admin/news/sources/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (s) => {
    try {
      await http.patch(`/admin/news/sources/${s.id}`, { enabled: !s.enabled });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await http.post("/admin/news/refresh");
      toast.success("Refresh queued — running in background. Check the runs log below in ~1-3 min.");
      setTimeout(load, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const recomputeTop25 = async () => {
    try {
      const { data } = await http.post("/admin/news/recompute-top25");
      toast.success(`Top 25 recomputed (${data.top25} stories)`);
    } catch { toast.error("Recompute failed"); }
  };

  const [backfilling, setBackfilling] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);

  const loadBackfillStatus = async () => {
    try {
      const { data } = await http.get("/admin/news/backfill-locations/status");
      setBackfillStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadBackfillStatus(); }, []);

  const runLocationBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const { data } = await http.post("/admin/news/backfill-locations", null, { params: { limit: 50 } });
      if (data.halted === "cooldown" || data.halted === "rate_limited") {
        toast.error("LLM daily budget hit — try again in 6 hours");
      } else {
        toast.success(`Backfilled ${data.successes}/${data.targets} articles`);
      }
      loadBackfillStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) return <div data-testid="admin-news-loading" className="py-12" style={{ color: "var(--wofy-text-soft)" }}>Loading news sources…</div>;

  return (
    <div data-testid="admin-news-sources">
      <AdminNewsCreate />

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <h2 className="font-display text-2xl">News Sources · {sources.length}</h2>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="admin-news-refresh-btn"
            onClick={refreshAll}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: refreshing ? "#eee" : "var(--wofy-primary)", color: refreshing ? "#666" : "white" }}
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh feeds now
          </button>
          <button
            data-testid="admin-news-recompute-btn"
            onClick={recomputeTop25}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            Recompute Top 25
          </button>
          <button
            data-testid="admin-news-location-backfill-btn"
            onClick={runLocationBackfill}
            disabled={backfilling}
            title={backfillStatus ? `${backfillStatus.missing} articles missing state/city tags` : "Fill in state/city tags"}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm border"
            style={{ borderColor: "var(--wofy-line)", background: backfilling ? "#eee" : "transparent" }}
          >
            📍 Backfill locations
            {backfillStatus && backfillStatus.missing > 0 && (
              <span className="ml-1 text-xs opacity-70">({backfillStatus.missing})</span>
            )}
          </button>
          <button
            data-testid="admin-news-add-btn"
            onClick={() => setAdding(!adding)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-semibold text-sm"
            style={{ background: "#5C6B52", color: "white" }}
          >
            <Plus size={13} /> Add source
          </button>
        </div>
      </div>

      {/* Add form */}
      {adding && (
        <div data-testid="admin-news-add-form" className="border rounded-lg p-4 mb-6" style={{ borderColor: "var(--wofy-line)", background: "rgba(0,0,0,0.02)" }}>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <input data-testid="src-name" className="border rounded px-3 py-2 text-sm" placeholder="Feed name (e.g. BBC — Business)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="src-url" className="border rounded px-3 py-2 text-sm" placeholder="RSS URL (https://…)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <select data-testid="src-category" className="border rounded px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input data-testid="src-country" className="border rounded px-3 py-2 text-sm" placeholder="Country (or Global)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              Weight (1-5)
              <input data-testid="src-weight" type="number" min="1" max="5" className="border rounded px-2 py-1 w-20 text-sm" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="src-create" onClick={create} className="px-4 py-2 rounded text-sm font-semibold" style={{ background: "var(--wofy-primary)", color: "white" }}>Add</button>
            <button onClick={() => { setAdding(false); setForm(EMPTY); }} className="px-4 py-2 rounded text-sm border" style={{ borderColor: "var(--wofy-line)" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Runs log */}
      {runs.length > 0 && (
        <div className="mb-6 border rounded p-3 text-xs" style={{ borderColor: "var(--wofy-line)", background: "rgba(0,0,0,0.02)" }}>
          <div className="font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>Recent ingestion runs</div>
          <ul className="space-y-1">
            {runs.map((r) => (
              <li key={r.id} data-testid={`admin-news-run-${r.id}`}>
                {new Date(r.ran_at).toLocaleString()} · sources: {r.sources} · found: {r.found} · new: {r.new} · dupes: {r.duplicates} · errors: {r.errors} · Top25: {r.top25}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sources table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left" style={{ borderBottom: "1px solid var(--wofy-line)" }}>
              <th className="py-2 pr-2">Name</th>
              <th className="py-2 pr-2">Category</th>
              <th className="py-2 pr-2">Country</th>
              <th className="py-2 pr-2 text-center">Weight</th>
              <th className="py-2 pr-2 text-center">Enabled</th>
              <th className="py-2 pr-2">Last fetch</th>
              <th className="py-2 pr-2 text-center">Articles</th>
              <th className="py-2 pr-2"></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => {
              const editing = editingId === s.id;
              return (
                <tr key={s.id} data-testid={`admin-news-src-row-${s.id}`} className="border-b" style={{ borderColor: "var(--wofy-line)" }}>
                  {editing ? (
                    <>
                      <td className="py-2 pr-2"><input className="border rounded px-2 py-1 text-sm w-full" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></td>
                      <td className="py-2 pr-2">
                        <select className="border rounded px-2 py-1 text-sm" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>
                          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-2"><input className="border rounded px-2 py-1 text-sm w-24" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} /></td>
                      <td className="py-2 pr-2 text-center"><input type="number" min="1" max="5" className="border rounded px-2 py-1 w-14 text-sm text-center" value={editForm.weight} onChange={(e) => setEditForm({ ...editForm, weight: Number(e.target.value) })} /></td>
                      <td className="py-2 pr-2 text-center"><input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} /></td>
                      <td className="py-2 pr-2 text-xs" style={{ color: "var(--wofy-text-soft)" }} colSpan={2}>
                        <input className="border rounded px-2 py-1 text-sm w-full" value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} />
                      </td>
                      <td className="py-2 pr-2">
                        <div className="flex gap-1">
                          <button data-testid={`admin-news-src-save-${s.id}`} onClick={saveEdit} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)" }}><Save size={13} /></button>
                          <button onClick={() => setEditingId(null)} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)" }}><X size={13} /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 pr-2">
                        <div className="font-semibold">{s.name}</div>
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs mt-0.5 hover:underline" style={{ color: "var(--wofy-text-soft)" }}>
                          <Rss size={10} /> {s.url.length > 60 ? s.url.slice(0, 60) + "…" : s.url}
                          <ExternalLink size={10} />
                        </a>
                        {s.fetch_error && <div className="text-xs mt-1" style={{ color: "#b23a3a" }}>⚠ {s.fetch_error}</div>}
                      </td>
                      <td className="py-2 pr-2">{s.category}</td>
                      <td className="py-2 pr-2">{s.country}</td>
                      <td className="py-2 pr-2 text-center">{s.weight}</td>
                      <td className="py-2 pr-2 text-center">
                        <button data-testid={`admin-news-src-toggle-${s.id}`} onClick={() => toggle(s)} title={s.enabled ? "Disable" : "Enable"}>
                          {s.enabled
                            ? <Power size={16} style={{ color: "#5C6B52" }} />
                            : <PowerOff size={16} style={{ color: "#b23a3a" }} />}
                        </button>
                      </td>
                      <td className="py-2 pr-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString() : "never"}</td>
                      <td className="py-2 pr-2 text-center">{s.article_count || 0}</td>
                      <td className="py-2 pr-2">
                        <div className="flex gap-1">
                          <button data-testid={`admin-news-src-edit-${s.id}`} onClick={() => { setEditingId(s.id); setEditForm(s); }} className="p-1.5 rounded border text-xs" style={{ borderColor: "var(--wofy-line)" }}>Edit</button>
                          <button data-testid={`admin-news-src-delete-${s.id}`} onClick={() => remove(s.id, s.name)} className="p-1.5 rounded border" style={{ borderColor: "var(--wofy-line)", color: "#b23a3a" }}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
